import type { AxiosInstance } from 'axios';
import { apiClient } from '../client/client';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { PaginatedList } from '../types/common/PaginatedList';
import type {
  SellerProfileViewModel,
  SellerRatingViewModel,
  SellerListingCardViewModel,
} from '../types/seller';

/**
 * Public seller profile endpoints introduced by feature 010.
 *
 * See `specs/010-seller-profile-utm-sharing/contracts/seller.http.md`.
 * All endpoints are `[AllowAnonymous]`; no JWT is required and no cookies are set.
 */
export class SellerService {
  private static readonly BASE = '/api/Seller';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async getProfile(
    userId: string,
  ): Promise<ServiceResultT<SellerProfileViewModel | null>> {
    const { data } = await this.client.get<
      ServiceResultT<SellerProfileViewModel | null>
    >(`${SellerService.BASE}/${userId}/profile`);
    return data;
  }

  async getRatings(
    userId: string,
    page?: number,
    pageSize?: number,
  ): Promise<ServiceResultT<PaginatedList<SellerRatingViewModel>>> {
    const { data } = await this.client.get<
      ServiceResultT<PaginatedList<SellerRatingViewModel>>
    >(`${SellerService.BASE}/${userId}/ratings`, {
      params: { page, pageSize },
    });
    return data;
  }

  async getListings(
    userId: string,
    page?: number,
    pageSize?: number,
  ): Promise<ServiceResultT<PaginatedList<SellerListingCardViewModel>>> {
    const { data } = await this.client.get<
      ServiceResultT<PaginatedList<SellerListingCardViewModel>>
    >(`${SellerService.BASE}/${userId}/listings`, {
      params: { page, pageSize },
    });
    return data;
  }
}

export const sellerService = new SellerService();
